import { useTranslation } from "react-i18next";
import React from "react";
import { Theme, useTheme, Box } from "@mui/material";
import {
  BookingDataConsistencyStatusEnum,
  BookingDataSourceEnum,
} from "@rentablo/api/apiv1";
import EditIcon from "@icons/myicons/edit.svg?react";
import LinkIcon from "@icons/myicons/links_thin.svg?react";
import ReturnIcon from "@icons/myicons/category_return.svg?react";
import WarningIcon from "@icons/myicons/warning.svg?react";
import DocumentShareArrowIcon from "@icons/myicons/document-file-share-arrow.svg?react";
import { Tooltip } from "@rentablo/components";

type TransactionStatus =
  | "edit"
  | "link"
  | "returned"
  | "warning"
  | "warningDelta"
  | "csv";

const mapStatusToIcon = (
  status: TransactionStatus,
  theme: Theme,
  size: number
) =>
  ({
    edit: (
      <EditIcon
        color={theme.palette.icon.secondary}
        width={size}
        height={size}
      />
    ),
    link: (
      <LinkIcon
        color={theme.palette.icon.secondary}
        width={size}
        height={size}
      />
    ),
    returned: (
      <ReturnIcon
        color={theme.palette.icon.warning}
        width={size}
        height={size}
      />
    ),
    warning: (
      <WarningIcon
        color={theme.palette.icon.warning}
        width={size}
        height={size}
      />
    ),
    warningDelta: (
      <WarningIcon
        color={theme.palette.icon.warning}
        width={size}
        height={size}
      />
    ),
    csv: (
      <DocumentShareArrowIcon
        color={theme.palette.icon.secondary}
        width={size}
        height={size}
      />
    ),
  })[status];

const getStatus = (
  isExternalID: boolean,
  dataConsistencyStatus?: BookingDataConsistencyStatusEnum,
  dataSource?: BookingDataSourceEnum
): TransactionStatus => {
  if (dataSource === BookingDataSourceEnum.CSV) {
    return "csv";
  }

  if (dataConsistencyStatus === BookingDataConsistencyStatusEnum.EXACT_NOL) {
    return isExternalID ? "link" : "edit";
  }

  if (
    dataConsistencyStatus ===
    BookingDataConsistencyStatusEnum.BOOKING_WITH_NOL_ESTIMATED
  ) {
    return "returned";
  }

  if (
    dataConsistencyStatus ===
    BookingDataConsistencyStatusEnum.NEGATIVE_NOL_DELTA_RESOLUTION
  ) {
    return "warning";
  }

  if (
    dataConsistencyStatus === BookingDataConsistencyStatusEnum.DELTA_RESOLUTION
  ) {
    return "warningDelta";
  }
  return "edit";
};

// Estimated or virtual bookings ask the user to check the data; the other
// statuses only say where the data came from.
const ATTENTION_STATUSES: TransactionStatus[] = [
  "returned",
  "warning",
  "warningDelta",
];

interface Props {
  dataConsistencyStatus?: BookingDataConsistencyStatusEnum;
  dataSource?: BookingDataSourceEnum;
  isExternalID: boolean;
}

export const transactionNeedsAttention = ({
  dataConsistencyStatus,
  dataSource,
  isExternalID,
}: Props) =>
  ATTENTION_STATUSES.includes(
    getStatus(isExternalID, dataConsistencyStatus, dataSource)
  );

export const TransactionStatusIcon = ({
  dataConsistencyStatus,
  dataSource,
  isExternalID,
  size = 18,
}: Props & { size?: number }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const status = getStatus(isExternalID, dataConsistencyStatus, dataSource);
  const title = t(`transactionsTable.status.${status}.title`);

  return (
    <Tooltip title={title} text={t(`transactionsTable.status.${status}.text`)}>
      <Box
        role="img"
        aria-label={title}
        display="inline-flex"
        alignItems="center"
        sx={{ cursor: "help" }}
      >
        {mapStatusToIcon(status, theme, size)}
      </Box>
    </Tooltip>
  );
};
